import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { bulkStore } from './bulkStore';
import { Meal, Symptom, WeeklyInsight, Exercise, ActionPlanItem, SavedRecipe, PlateDish, MealIngredientItem, CustomFoodItem, UserFoodCorrection } from './types';
import { INITIAL_MEALS, INITIAL_SYMPTOMS, INITIAL_INSIGHTS } from './mockData';
import {
  convertTagsToIngredientItems,
  getRealisticCulinaryServing,
  getSafeLimitNote,
  calculatePlateFodmapStacking,
  getDynamicIngredientRating,
  FodmapFoodItem,
} from './fodmapEngine';

const STORAGE_KEYS = {
  MEALS: 'digestlog_meals_v1',
  SYMPTOMS: 'digestlog_symptoms_v1',
  EXERCISES: 'digestlog_exercises_v1',
  INSIGHTS: 'digestlog_insights_v1',
  INSIGHTS_ARCHIVE: 'digestlog_insights_archive_v1',
  RECIPES: 'digestlog_saved_recipes_v1',
  INITIALIZED: 'digestlog_initialized_v1',
  LAST_AI_RUN: 'digestlog_last_ai_run_v1',
  ACTION_PLANS: 'digestlog_action_plans_v1',
  ACTION_PLAN_ROTATION: 'digestlog_action_plan_rotation_v1',
  CLARIFICATIONS: 'digestlog_clarifications_v2',
  ANSWERED_CLARIFICATIONS: 'digestlog_answered_clarifications_v1',
  CUSTOM_FOODS: 'digestlog_custom_foods_v1',
  SHARED_CLOUD_FOODS: 'digestlog_shared_foods_v1',
  HIDDEN_FOOD_IDS: 'digestlog_hidden_food_ids_v1',
  FOOD_OVERRIDES: 'digestlog_food_overrides_v1',
  USER_FOOD_CORRECTIONS: 'digestlog_user_food_corrections_v1',
  SYMPTOM_REPAIR_BACKUP: 'digestlog_symptoms_backup_pre_repair_v1',
};

function getKey(baseKey: string, userId?: string | null): string {
  if (!userId || userId === 'usr_harry') return baseKey;
  return `${baseKey}_${userId}`;
}

/*
 * On the phone only the newest few insights stay in localStorage (digestlog_insights_v1, same format
 * as before); older ones move to native storage under INSIGHTS_ARCHIVE. The archive is held in memory
 * once loaded and only ever gains entries, so a save made before it loads can't drop old insights.
 */
const INSIGHTS_KEPT_IN_LOCALSTORAGE = 3;
const insightsArchive = new Map<string, WeeklyInsight[]>();
const insightsArchiveLoading = new Map<string, Promise<WeeklyInsight[]>>();
let insightsSaveChain: Promise<unknown> = Promise.resolve();

function insightKey(i: WeeklyInsight): string {
  return `${i.generated_at_iso}|${i.week_range}`;
}

export function dedupeInsights(list: WeeklyInsight[]): WeeklyInsight[] {
  const seen = new Set<string>();
  return list.filter((i) => {
    if (!i) return false;
    const k = insightKey(i);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function loadInsightsArchive(archiveKey: string): Promise<WeeklyInsight[]> {
  const cached = insightsArchive.get(archiveKey);
  if (cached) return Promise.resolve(cached);
  let loading = insightsArchiveLoading.get(archiveKey);
  if (!loading) {
    loading = bulkStore
      .get(archiveKey)
      .then((raw) => {
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? (parsed as WeeklyInsight[]) : [];
      })
      .then((list) => {
        insightsArchive.set(archiveKey, list);
        insightsArchiveLoading.delete(archiveKey);
        return list;
      });
    insightsArchiveLoading.set(archiveKey, loading);
  }
  return loading;
}

function clearInsightsArchive(userId?: string | null): void {
  const archiveKey = getKey(STORAGE_KEYS.INSIGHTS_ARCHIVE, userId);
  insightsArchive.set(archiveKey, []);
  if (!Capacitor.isNativePlatform()) return;
  insightsSaveChain = insightsSaveChain
    .then(() => bulkStore.remove(archiveKey))
    .catch((e) => console.error('Failed to clear insights archive', e));
}

/** Saves the full list on the phone: older entries to the archive first, then trims localStorage once that has been read back. */
async function saveInsightsNative(insights: WeeklyInsight[], userId?: string | null): Promise<void> {
  const key = getKey(STORAGE_KEYS.INSIGHTS, userId);
  const archiveKey = getKey(STORAGE_KEYS.INSIGHTS_ARCHIVE, userId);
  const recent = insights.slice(0, INSIGHTS_KEPT_IN_LOCALSTORAGE);
  const recentKeys = new Set(recent.map(insightKey));
  try {
    const existing = await loadInsightsArchive(archiveKey);
    const archive = dedupeInsights([...insights.slice(INSIGHTS_KEPT_IN_LOCALSTORAGE), ...existing]).filter(
      (i) => !recentKeys.has(insightKey(i))
    );
    const archived = archive.length === 0 && existing.length === 0 ? true : await bulkStore.setVerified(archiveKey, JSON.stringify(archive));
    if (archived) {
      insightsArchive.set(archiveKey, archive);
      localStorage.setItem(key, JSON.stringify(recent));
      return;
    }
  } catch (e) {
    console.error('Failed to archive older insights', e);
  }
  // Archive unavailable: keep everything in localStorage as before.
  localStorage.setItem(key, JSON.stringify(insights));
}

export const STORAGE_ERROR_EVENT = 'digestlog_storage_error';

export interface StorageErrorDetail {
  what: 'meals' | 'symptoms' | 'exercises';
  quotaExceeded: boolean;
  message: string;
}

/** Tells the UI that a local save failed, so it isn't only visible in the console. */
function reportSaveFailure(what: StorageErrorDetail['what'], error: unknown): void {
  console.error(`Failed to save ${what}`, error);
  const quotaExceeded =
    error instanceof DOMException && (error.name === 'QuotaExceededError' || error.code === 22 || error.name === 'NS_ERROR_DOM_QUOTA_REACHED');
  const detail: StorageErrorDetail = {
    what,
    quotaExceeded,
    message: error instanceof Error ? error.message : String(error),
  };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<StorageErrorDetail>(STORAGE_ERROR_EVENT, { detail }));
  }
}

export const DEFAULT_GEMINI_MODEL = 'v1beta/gemini-3.5-flash-lite';

export function convert5ScaleTo10Scale(val: number): number {
  if (val <= 1) return 0;
  if (val === 2) return 4;
  if (val === 3) return 6;
  if (val === 4) return 8;
  if (val >= 5) return 10;
  return Math.round(val * 2);
}

/** Check-ins saved from the 0–10 sliders record their values in the tags, e.g. "Bloating (3/10)". */
export function isTenScaleSymptom(s: Pick<Symptom, 'symptom_tags'>): boolean {
  return /\(\d+(?:\.\d+)?\/10\)/.test(s.symptom_tags || '');
}

export function normalizeSymptomScores(s: Symptom): Symptom {
  const scores: Record<string, number> = s.symptom_scores ? { ...s.symptom_scores } : {};
  // Already on the 0–10 scale: the legacy 1–5 remaps below would inflate genuine values (2→4, 3→6).
  const isTenScale = isTenScaleSymptom(s);

  const rawBloat = s.bloating_score;
  const rawSoreness = s.soreness_score;
  const rawTaste = s.bad_taste_score;

  // Bloating normalization
  if (scores.bloating === undefined) {
    const b = rawBloat ?? 0;
    scores.bloating = b <= 5 ? convert5ScaleTo10Scale(b) : b;
  } else if (isTenScale) {
    // keep as entered
  } else if (scores.bloating === 1 && (rawBloat === 1 || rawBloat === undefined)) {
    // 1 in old scale was "None", should be 0
    scores.bloating = 0;
  } else if (scores.bloating === 2 && rawBloat === 2) {
    scores.bloating = 4;
  } else if (scores.bloating === 3 && (rawBloat === 2 || rawBloat === 3)) {
    scores.bloating = rawBloat === 2 ? 4 : 6;
  } else if (scores.bloating === 5 && rawBloat === 3) {
    scores.bloating = 6;
  }

  // Stomach Pain normalization
  if (scores.stomach_pain === undefined) {
    const p = rawSoreness ?? 0;
    scores.stomach_pain = p <= 5 ? convert5ScaleTo10Scale(p) : p;
  } else if (isTenScale) {
    // keep as entered
  } else if (scores.stomach_pain === 1 && (rawSoreness === 1 || rawSoreness === undefined)) {
    scores.stomach_pain = 0;
  } else if (scores.stomach_pain === 2 && rawSoreness === 2) {
    scores.stomach_pain = 4;
  } else if (scores.stomach_pain === 3 && (rawSoreness === 2 || rawSoreness === 3)) {
    scores.stomach_pain = rawSoreness === 2 ? 4 : 6;
  } else if (scores.stomach_pain === 5 && rawSoreness === 3) {
    scores.stomach_pain = 6;
  }

  // Mouth Taste normalization
  if (scores.mouth_taste === undefined && rawTaste !== undefined && rawTaste !== null) {
    scores.mouth_taste = rawTaste <= 5 ? convert5ScaleTo10Scale(rawTaste) : rawTaste;
  } else if (isTenScale) {
    // keep as entered
  } else if (scores.mouth_taste === 1 && rawTaste === 1) {
    scores.mouth_taste = 0;
  } else if (scores.mouth_taste === 2 && rawTaste === 2) {
    scores.mouth_taste = 4;
  } else if (scores.mouth_taste === 3 && (rawTaste === 2 || rawTaste === 3)) {
    scores.mouth_taste = rawTaste === 2 ? 4 : 6;
  }

  // Parse legacy symptom_tags if present
  // Free-text tags from older versions; 0–10 check-ins already carry exact scores.
  if (!isTenScale) {
    const tags = (s.symptom_tags || '').toLowerCase();
    if (scores.heartburn === undefined && /heartburn|acid/i.test(tags)) scores.heartburn = 6;
    if (scores.reflux === undefined && /reflux|regurgitat/i.test(tags)) scores.reflux = 6;
    if (scores.nausea === undefined && /nausea|queasy/i.test(tags)) scores.nausea = 6;
    if (scores.vomiting === undefined && /vomit|emesis/i.test(tags)) scores.vomiting = 8;
    if (scores.diarrhea === undefined && /diarrhea|loose/i.test(tags)) scores.diarrhea = 6;
    if (scores.constipation === undefined && /constipation|hard stool/i.test(tags)) scores.constipation = 6;
    if (scores.headache === undefined && /headache|migraine/i.test(tags)) scores.headache = 6;
    if (scores.eczema === undefined && /eczema|flush|itch/i.test(tags)) scores.eczema = 6;
    if (scores.hayfever === undefined && /hayfever|nasal|sinus|sneez/i.test(tags)) scores.hayfever = 6;
    if (scores.asthma === undefined && /asthma|wheez|breath/i.test(tags)) scores.asthma = 6;
  }

  return {
    ...s,
    bloating_score: scores.bloating ?? 0,
    soreness_score: scores.stomach_pain ?? 0,
    bad_taste_score: scores.mouth_taste,
    symptom_scores: scores,
  };
}

export const localStore = {
  getMeals(userId?: string | null): Meal[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.MEALS, userId));
      if (data !== null) {
        const rawParsed: Meal[] = JSON.parse(data);
        const isMockMeal = (m: Meal) =>
          ['m-1', 'm-2', 'm-3', 'm-4'].includes(m.id) ||
          /oatmeal with blueberries|garlic parmesan fettuccine|grilled chicken salad with quinoa|green tea and sourdough/i.test(m.description || '');
        const parsed = rawParsed.filter((m) => !isMockMeal(m));
        if (parsed.length !== rawParsed.length) {
          localStorage.setItem(getKey(STORAGE_KEYS.MEALS, userId), JSON.stringify(parsed));
        }
        const needsCalibration = parsed.some((m) => !m.dishes || m.dishes.length === 0 || !m.stacking_report);
        if (!needsCalibration) {
          return parsed;
        }
        let anyChanged = false;
        const calibrated = parsed.map((m) => {
          if (m.dishes && m.dishes.length > 0 && m.stacking_report) return m;
          const { meal, changed } = calibrateMealIngredientPortions(m);
          if (changed) anyChanged = true;
          return meal;
        });
        if (anyChanged) {
          localStorage.setItem(getKey(STORAGE_KEYS.MEALS, userId), JSON.stringify(calibrated));
        }
        return calibrated;
      }
    } catch (e) {
      console.error('Failed to read meals from storage', e);
    }
    return [];
  },

  saveMeals(meals: Meal[], userId?: string | null): void {
    try {
      const isMockMeal = (m: Meal) =>
        ['m-1', 'm-2', 'm-3', 'm-4'].includes(m.id) ||
        /oatmeal with blueberries|garlic parmesan fettuccine|grilled chicken salad with quinoa|green tea and sourdough/i.test(m.description || '');
      const cleaned = meals.filter((m) => !isMockMeal(m));
      localStorage.setItem(getKey(STORAGE_KEYS.INITIALIZED, userId), 'true');
      localStorage.setItem(getKey(STORAGE_KEYS.MEALS, userId), JSON.stringify(cleaned));
    } catch (e) {
      reportSaveFailure('meals', e);
    }
  },

  getSymptoms(userId?: string | null): Symptom[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.SYMPTOMS, userId));
      if (data !== null) {
        const rawParsed: Symptom[] = JSON.parse(data);
        const isMockSymptom = (s: Symptom) => ['s-1', 's-2', 's-3'].includes(s.id);
        const parsed = rawParsed.filter((s) => !isMockSymptom(s));
        if (parsed.length !== rawParsed.length) {
          localStorage.setItem(getKey(STORAGE_KEYS.SYMPTOMS, userId), JSON.stringify(parsed));
        }
        return parsed.map(normalizeSymptomScores);
      }
    } catch (e) {
      console.error('Failed to read symptoms from storage', e);
    }
    return [];
  },

  /** Symptoms exactly as stored on this device, without any read-time normalization. */
  getRawSymptoms(userId?: string | null): Symptom[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.SYMPTOMS, userId));
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Failed to read raw symptoms', e);
      return [];
    }
  },

  /**
   * Write-once safety copy taken before any score repair. Never overwritten once it exists.
   * On the phone it lives in native app storage: WebKit's ~5 MB localStorage quota is
   * already used up by meal history, so a second copy of the symptoms won't fit there.
   */
  async ensureSymptomRepairBackup(userId?: string | null): Promise<{ createdAt: string; count: number } | null> {
    const key = getKey(STORAGE_KEYS.SYMPTOM_REPAIR_BACKUP, userId);
    try {
      const existing = await localStore.getSymptomRepairBackup(userId);
      if (existing) return { createdAt: existing.createdAt, count: existing.symptoms.length };

      const raw = localStorage.getItem(getKey(STORAGE_KEYS.SYMPTOMS, userId));
      const symptoms: Symptom[] = raw ? JSON.parse(raw) : [];
      const createdAt = new Date().toISOString();
      const value = JSON.stringify({ createdAt, symptoms });
      if (Capacitor.isNativePlatform()) {
        await Preferences.set({ key, value });
      } else {
        localStorage.setItem(key, value);
      }
      // Read it back so we never report a backup that didn't actually persist.
      const check = await localStore.getSymptomRepairBackup(userId);
      return check && check.symptoms.length === symptoms.length ? { createdAt, count: symptoms.length } : null;
    } catch (e) {
      console.error('Failed to create symptom repair backup', e);
      return null;
    }
  },

  async getSymptomRepairBackup(userId?: string | null): Promise<{ createdAt: string; symptoms: Symptom[] } | null> {
    const key = getKey(STORAGE_KEYS.SYMPTOM_REPAIR_BACKUP, userId);
    try {
      const raw = Capacitor.isNativePlatform()
        ? (await Preferences.get({ key })).value ?? localStorage.getItem(key)
        : localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  saveSymptoms(symptoms: Symptom[], userId?: string | null): void {
    try {
      const normalized = symptoms.map(normalizeSymptomScores);
      localStorage.setItem(getKey(STORAGE_KEYS.INITIALIZED, userId), 'true');
      localStorage.setItem(getKey(STORAGE_KEYS.SYMPTOMS, userId), JSON.stringify(normalized));
    } catch (e) {
      reportSaveFailure('symptoms', e);
    }
  },

  getExercises(userId?: string | null): Exercise[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.EXERCISES, userId));
      if (data !== null) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.error('Failed to read exercises from storage', e);
    }
    return [];
  },

  saveExercises(exercises: Exercise[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.INITIALIZED, userId), 'true');
      localStorage.setItem(getKey(STORAGE_KEYS.EXERCISES, userId), JSON.stringify(exercises));
    } catch (e) {
      reportSaveFailure('exercises', e);
    }
  },

  getInsights(userId?: string | null): WeeklyInsight[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.INSIGHTS, userId));
      const archive = insightsArchive.get(getKey(STORAGE_KEYS.INSIGHTS_ARCHIVE, userId)) || [];
      if (data !== null) return archive.length > 0 ? dedupeInsights([...JSON.parse(data), ...archive]) : JSON.parse(data);
      if (archive.length > 0) return archive;
    } catch (e) {
      console.error('Failed to read insights', e);
    }
    const isInit = localStorage.getItem(getKey(STORAGE_KEYS.INITIALIZED, userId));
    if (userId && userId !== 'usr_harry') return [];
    return isInit ? [] : INITIAL_INSIGHTS;
  },

  saveInsights(insights: WeeklyInsight[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.INITIALIZED, userId), 'true');
      if (!Capacitor.isNativePlatform()) {
        localStorage.setItem(getKey(STORAGE_KEYS.INSIGHTS, userId), JSON.stringify(insights));
        return;
      }
      // Saves run one after another so an older list can't land after a newer one.
      insightsSaveChain = insightsSaveChain
        .then(() => saveInsightsNative(insights, userId))
        .catch((e) => console.error('Failed to save insights', e));
    } catch (e) {
      console.error('Failed to save insights', e);
    }
  },

  /**
   * Loads the native insights archive and moves older insights out of localStorage.
   * Resolves with the full list (localStorage + archive). Web: just the localStorage list.
   */
  async loadInsights(userId?: string | null): Promise<WeeklyInsight[]> {
    if (!Capacitor.isNativePlatform()) return localStore.getInsights(userId);
    try {
      await loadInsightsArchive(getKey(STORAGE_KEYS.INSIGHTS_ARCHIVE, userId));
      const raw = localStorage.getItem(getKey(STORAGE_KEYS.INSIGHTS, userId));
      const stored: WeeklyInsight[] = raw ? JSON.parse(raw) : [];
      if (Array.isArray(stored) && stored.length > INSIGHTS_KEPT_IN_LOCALSTORAGE) {
        await (insightsSaveChain = insightsSaveChain.then(() => saveInsightsNative(localStore.getInsights(userId), userId)).catch((e) => console.error('Failed to move older insights', e)));
      }
    } catch (e) {
      console.error('Failed to load insights archive', e);
    }
    return localStore.getInsights(userId);
  },

  clearAll(userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.INITIALIZED, userId), 'true');
      localStorage.setItem(getKey(STORAGE_KEYS.MEALS, userId), JSON.stringify([]));
      localStorage.setItem(getKey(STORAGE_KEYS.SYMPTOMS, userId), JSON.stringify([]));
      localStorage.setItem(getKey(STORAGE_KEYS.EXERCISES, userId), JSON.stringify([]));
      localStorage.setItem(getKey(STORAGE_KEYS.INSIGHTS, userId), JSON.stringify([]));
      clearInsightsArchive(userId);
    } catch (e) {
      console.error('Failed to clear local store', e);
    }
  },

  clearUserData(userId?: string | null): void {
    try {
      const targetUserId = userId || 'usr_harry';
      const keysToRemove = [
        getKey(STORAGE_KEYS.MEALS, targetUserId),
        getKey(STORAGE_KEYS.SYMPTOMS, targetUserId),
        getKey(STORAGE_KEYS.EXERCISES, targetUserId),
        getKey(STORAGE_KEYS.INSIGHTS, targetUserId),
        getKey(STORAGE_KEYS.INITIALIZED, targetUserId),
        getKey('digestlog_user_profile', targetUserId),
        getKey('digestlog_action_goals', targetUserId),
        getKey(STORAGE_KEYS.ACTION_PLANS, targetUserId),
        getKey(STORAGE_KEYS.ACTION_PLAN_ROTATION, targetUserId),
      ];
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      clearInsightsArchive(targetUserId);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('digestlog_biometrics_') && (targetUserId === 'usr_harry' ? !k.includes('_usr_') : k.includes(targetUserId))) {
          localStorage.removeItem(k);
        }
      }
    } catch (e) {
      console.error('Failed to clear user data', e);
    }
  },

  loadSampleData(): void {
    try {
      localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');
      localStorage.setItem(STORAGE_KEYS.MEALS, JSON.stringify(INITIAL_MEALS));
      localStorage.setItem(STORAGE_KEYS.SYMPTOMS, JSON.stringify(INITIAL_SYMPTOMS));
      localStorage.setItem(STORAGE_KEYS.INSIGHTS, JSON.stringify(INITIAL_INSIGHTS));
    } catch (e) {
      console.error('Failed to load sample data', e);
    }
  },

  getSavedRecipes(userId?: string | null): SavedRecipe[] {
    try {
      const data = localStorage.getItem(getKey(STORAGE_KEYS.RECIPES, userId));
      if (data !== null) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to read recipes from storage', e);
    }
    return INITIAL_SAVED_RECIPES;
  },

  saveSavedRecipes(recipes: SavedRecipe[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.RECIPES, userId), JSON.stringify(recipes));
    } catch (e) {
      console.error('Failed to save recipes', e);
    }
  },

  saveRecipe(recipe: SavedRecipe, userId?: string | null): void {
    const list = this.getSavedRecipes(userId);
    const existingIndex = list.findIndex((r) => r.id === recipe.id);
    if (existingIndex >= 0) {
      list[existingIndex] = recipe;
    } else {
      list.unshift(recipe);
    }
    this.saveSavedRecipes(list, userId);
  },

  deleteRecipe(recipeId: string, userId?: string | null): void {
    const list = this.getSavedRecipes(userId).filter((r) => r.id !== recipeId);
    this.saveSavedRecipes(list, userId);
  },

  getCustomFoods(): CustomFoodItem[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CUSTOM_FOODS);
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to get custom foods', e);
    }
    return [];
  },

  saveCustomFoods(foods: CustomFoodItem[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.CUSTOM_FOODS, JSON.stringify(foods));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_foods_updated'));
      }
    } catch (e) {
      console.error('Failed to save custom foods', e);
    }
  },

  getSharedCloudFoods(): CustomFoodItem[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.SHARED_CLOUD_FOODS);
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to get shared cloud foods', e);
    }
    return [];
  },

  saveSharedCloudFoods(foods: CustomFoodItem[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.SHARED_CLOUD_FOODS, JSON.stringify(foods));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_foods_updated'));
      }
    } catch (e) {
      console.error('Failed to save shared cloud foods', e);
    }
  },

  getHiddenFoodIds(): string[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.HIDDEN_FOOD_IDS);
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to get hidden food ids', e);
    }
    return [];
  },

  saveHiddenFoodIds(ids: string[]): void {
    try {
      localStorage.setItem(STORAGE_KEYS.HIDDEN_FOOD_IDS, JSON.stringify(ids));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_foods_updated'));
      }
    } catch (e) {
      console.error('Failed to save hidden food ids', e);
    }
  },

  getFoodOverrides(): Record<string, Partial<FodmapFoodItem>> {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.FOOD_OVERRIDES);
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to get food overrides', e);
    }
    return {};
  },

  saveFoodOverrides(overrides: Record<string, Partial<FodmapFoodItem>>): void {
    try {
      localStorage.setItem(STORAGE_KEYS.FOOD_OVERRIDES, JSON.stringify(overrides));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_foods_updated'));
      }
    } catch (e) {
      console.error('Failed to save food overrides', e);
    }
  },

  getUserFoodCorrections(): Record<string, UserFoodCorrection> {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.USER_FOOD_CORRECTIONS);
      if (data) return JSON.parse(data);
    } catch (e) {
      console.error('Failed to get user food corrections', e);
    }
    return {};
  },

  saveUserFoodCorrection(correction: UserFoodCorrection): void {
    try {
      const existing = this.getUserFoodCorrections();
      const key = correction.foodTag.toLowerCase().trim();
      existing[key] = {
        ...correction,
        foodTag: key,
        createdAtIso: correction.createdAtIso || new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEYS.USER_FOOD_CORRECTIONS, JSON.stringify(existing));
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_user_corrections_updated'));
      }
    } catch (e) {
      console.error('Failed to save user food correction', e);
    }
  },

  removeUserFoodCorrection(foodTag: string): void {
    try {
      const existing = this.getUserFoodCorrections();
      const key = foodTag.toLowerCase().trim();
      if (existing[key]) {
        delete existing[key];
        localStorage.setItem(STORAGE_KEYS.USER_FOOD_CORRECTIONS, JSON.stringify(existing));
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('digestlog_user_corrections_updated'));
        }
      }
    } catch (e) {
      console.error('Failed to remove user food correction', e);
    }
  },

  getLastAiRun(): number | null {
    try {
      const v = localStorage.getItem(STORAGE_KEYS.LAST_AI_RUN);
      return v ? parseInt(v, 10) : null;
    } catch {
      return null;
    }
  },

  setLastAiRun(timestamp: number): void {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_AI_RUN, String(timestamp));
    } catch {}
  },

  getGeminiApiKey(): string {
    try {
      return localStorage.getItem('digestlog_gemini_api_key') || '';
    } catch {
      return '';
    }
  },

  saveGeminiApiKey(key: string): void {
    try {
      localStorage.setItem('digestlog_gemini_api_key', key.trim());
    } catch (e) {
      console.error('Failed to save Gemini API key', e);
    }
  },

  getGeminiModel(): string {
    try {
      const model = localStorage.getItem('digestlog_gemini_model');
      if (!model) {
        return DEFAULT_GEMINI_MODEL;
      }
      return model.trim();
    } catch {
      return DEFAULT_GEMINI_MODEL;
    }
  },

  saveGeminiModel(model: string): void {
    try {
      localStorage.setItem('digestlog_gemini_model', model.trim());
    } catch (e) {
      console.error('Failed to save Gemini model', e);
    }
  },

  getCurrentUser(): { userId: string; accountId: string; role?: string; displayName?: string } | null {
    try {
      const raw = localStorage.getItem('digestlog_auth_user');
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  },

  getCurrentUserId(): string {
    const user = this.getCurrentUser();
    return user?.userId || 'usr_harry';
  },

  getBaselineSettings(): {
    autoCalculate: boolean;
    restingBloat: number;
    restingSoreness: number;
    restingWellbeing: number;
  } {
    try {
      const raw = localStorage.getItem('digestlog_gut_baseline');
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      autoCalculate: true,
      restingBloat: 2.0,
      restingSoreness: 2.0,
      restingWellbeing: 3.0,
    };
  },

  saveBaselineSettings(settings: {
    autoCalculate: boolean;
    restingBloat: number;
    restingSoreness: number;
    restingWellbeing: number;
  }): void {
    try {
      localStorage.setItem('digestlog_gut_baseline', JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save gut baseline', e);
    }
  },

  getFodmapTrackingEnabled(userId?: string | null): boolean {
    try {
      const raw = localStorage.getItem(getKey('digestlog_fodmap_enabled', userId));
      if (raw !== null) {
        return raw === 'true';
      }
    } catch {}
    return true; // Default enabled
  },

  saveFodmapTrackingEnabled(enabled: boolean, userId?: string | null): void {
    try {
      localStorage.setItem(getKey('digestlog_fodmap_enabled', userId), enabled ? 'true' : 'false');
      // Dispatch a custom event so other components update synchronously without full page reload
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('digestlog_fodmap_toggle', { detail: { enabled } }));
      }
    } catch (e) {
      console.error('Failed to save FODMAP tracking preference', e);
    }
  },

  getUserProfile(userId?: string | null, accountId?: string | null): any {
    try {
      const raw = localStorage.getItem(getKey('digestlog_user_profile', userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    const isHarry = !userId || userId === 'usr_harry';
    return {
      userId: userId || 'usr_harry',
      accountId: accountId || (isHarry ? 'Harry' : userId ? userId.replace('usr_', '') : 'acc_79841'),
      name: isHarry ? 'Harry Elliott' : (accountId || 'User'),
      avatarUrl: '',
      transitSpeed: 'Normal',
      bowelFrequency: '1-2 times daily',
    };
  },

  saveUserProfile(profile: any, userId?: string | null): void {
    try {
      localStorage.setItem(getKey('digestlog_user_profile', userId), JSON.stringify(profile));
    } catch (e) {
      console.error('Failed to save user profile', e);
    }
  },

  getBiometrics(dateStr: string, userId?: string | null): any {
    try {
      const raw = localStorage.getItem(getKey(`digestlog_biometrics_${dateStr}`, userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      date: dateStr,
      sleep_quality: undefined,
      stress_level: undefined,
      avoiding_foods: '',
      poo_logged: false,
    };
  },

  saveBiometrics(biometrics: any, userId?: string | null): void {
    try {
      localStorage.setItem(getKey(`digestlog_biometrics_${biometrics.date}`, userId), JSON.stringify(biometrics));
    } catch (e) {
      console.error('Failed to save biometrics', e);
    }
  },

  getActionGoals(userId?: string | null): any[] {
    try {
      const raw = localStorage.getItem(getKey('digestlog_action_goals', userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    return [
      {
        id: 'goal-garlic-onion',
        title: 'Disentangle: Garlic vs. Onion',
        description: 'Prepare a plain steamed rice & chicken dinner seasoned only with garlic oil (no allium solids). This helps isolate allicin from fructan bloat.',
        test_meal: 'Steamed Chicken with Pure Garlic Infused Oil',
        status: 'pending',
        created_at_iso: new Date().toISOString(),
      },
    ];
  },

  saveActionGoals(goals: any[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey('digestlog_action_goals', userId), JSON.stringify(goals));
    } catch (e) {
      console.error('Failed to save action goals', e);
    }
  },

  getAnsweredClarifications(userId?: string | null): Record<string, { option: string; answeredAt: string; mealId?: string }> {
    try {
      const raw = localStorage.getItem(getKey(STORAGE_KEYS.ANSWERED_CLARIFICATIONS, userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    return {};
  },

  saveAnsweredClarification(
    questionId: string,
    answer: { option: string; answeredAt: string; mealId?: string },
    userId?: string | null
  ): void {
    try {
      const current = this.getAnsweredClarifications(userId);
      current[questionId] = answer;
      if (answer.mealId) {
        // Also index by mealId to prevent duplicate questions for the same meal
        current[`meal_${answer.mealId}`] = answer;
      }
      localStorage.setItem(getKey(STORAGE_KEYS.ANSWERED_CLARIFICATIONS, userId), JSON.stringify(current));
    } catch (e) {
      console.error('Failed to save answered clarification', e);
    }
  },

  getClarifications(userId?: string | null): any[] {
    try {
      const raw = localStorage.getItem(getKey(STORAGE_KEYS.CLARIFICATIONS, userId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const answered = this.getAnsweredClarifications(userId);
          return parsed.map((item) => {
            if (answered[item.id]) {
              return { ...item, answered_option: answered[item.id].option, answered_at: answered[item.id].answeredAt };
            }
            if (item.meal_id && answered[`meal_${item.meal_id}`]) {
              return { ...item, answered_option: answered[`meal_${item.meal_id}`].option, answered_at: answered[`meal_${item.meal_id}`].answeredAt };
            }
            return item;
          });
        }
      }
    } catch {}
    return [];
  },

  saveClarifications(items: any[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.CLARIFICATIONS, userId), JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save clarifications', e);
    }
  },

  getActionPlans(userId?: string | null): ActionPlanItem[] {
    try {
      const raw = localStorage.getItem(getKey(STORAGE_KEYS.ACTION_PLANS, userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    return DEFAULT_ACTION_PLANS;
  },

  saveActionPlans(plans: ActionPlanItem[], userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.ACTION_PLANS, userId), JSON.stringify(plans));
    } catch (e) {
      console.error('Failed to save action plans', e);
    }
  },

  getActionPlanState(userId?: string | null): { activeIndex: number; lastDate: string } {
    try {
      const raw = localStorage.getItem(getKey(STORAGE_KEYS.ACTION_PLAN_ROTATION, userId));
      if (raw) return JSON.parse(raw);
    } catch {}
    return { activeIndex: 0, lastDate: '' };
  },

  saveActionPlanState(state: { activeIndex: number; lastDate: string }, userId?: string | null): void {
    try {
      localStorage.setItem(getKey(STORAGE_KEYS.ACTION_PLAN_ROTATION, userId), JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save action plan rotation state', e);
    }
  },

  getOrRotateActionPlan(userId?: string | null, todayStr?: string): { plan: ActionPlanItem; index: number; total: number; allPlans: ActionPlanItem[] } {
    const plans = this.getActionPlans(userId);
    if (plans.length === 0) {
      return { plan: DEFAULT_ACTION_PLANS[0], index: 0, total: DEFAULT_ACTION_PLANS.length, allPlans: DEFAULT_ACTION_PLANS };
    }
    const dateStr = todayStr || new Date().toISOString().split('T')[0];
    const state = this.getActionPlanState(userId);

    let activeIdx = state.activeIndex % plans.length;
    // Rotate each day if untouched ('pending')
    if (state.lastDate && state.lastDate !== dateStr) {
      if (plans[activeIdx].status === 'pending') {
        activeIdx = (activeIdx + 1) % plans.length;
      }
      this.saveActionPlanState({ activeIndex: activeIdx, lastDate: dateStr }, userId);
    } else if (!state.lastDate) {
      this.saveActionPlanState({ activeIndex: activeIdx, lastDate: dateStr }, userId);
    }

    return { plan: plans[activeIdx], index: activeIdx, total: plans.length, allPlans: plans };
  },

  skipCurrentActionPlan(userId?: string | null): { plan: ActionPlanItem; index: number } {
    const plans = this.getActionPlans(userId);
    const state = this.getActionPlanState(userId);
    let activeIdx = state.activeIndex % plans.length;
    plans[activeIdx].status = 'skipped';
    this.saveActionPlans(plans, userId);

    const nextIdx = (activeIdx + 1) % plans.length;
    const dateStr = new Date().toISOString().split('T')[0];
    this.saveActionPlanState({ activeIndex: nextIdx, lastDate: dateStr }, userId);
    return { plan: plans[nextIdx], index: nextIdx };
  },

  acceptCurrentActionPlan(userId?: string | null): ActionPlanItem {
    const plans = this.getActionPlans(userId);
    const state = this.getActionPlanState(userId);
    let activeIdx = state.activeIndex % plans.length;
    plans[activeIdx].status = 'accepted';
    this.saveActionPlans(plans, userId);
    return plans[activeIdx];
  },
};

export const DEFAULT_ACTION_PLANS: ActionPlanItem[] = [
  {
    id: 'plan-allium-reset',
    title: '72-Hour Allium Elimination',
    description: 'Strictly eliminate garlic, onions, shallots, and leeks for 3 days to test mucosal settling and reduce delayed waking distension.',
    category: 'elimination',
    target_triggers: ['Alliums'],
    avoid_ingredients: ['Garlic', 'Onion', 'Shallots', 'Leeks', 'Garlic powder', 'Onion powder'],
    recommended_dish: 'Ginger & Turmeric Poached Chicken with Jasmine Rice',
    status: 'pending',
  },
  {
    id: 'plan-dairy-swap',
    title: 'Conventional Dairy Pause & Swap',
    description: 'Pause cows milk, cheese, and cream for 72h. Substitute with unsweetened oat or coconut milk to assess upper GI acidity and gut clarity.',
    category: 'substitution',
    target_triggers: ['Conventional Dairy'],
    avoid_ingredients: ['Milk', 'Cheese', 'Butter', 'Cream', 'Yogurt'],
    recommended_dish: 'Herb-Crusted Wild Salmon with Roasted Zucchini & Quinoa',
    status: 'pending',
  },
  {
    id: 'plan-gluten-free',
    title: 'Gluten & Wheat Elimination',
    description: 'Avoid wheat flour, standard breads, and pasta. Anchor your carbs on steamed jasmine rice, quinoa, and certified gluten-free oats.',
    category: 'elimination',
    target_triggers: ['Gluten'],
    avoid_ingredients: ['Wheat', 'Barley', 'Rye', 'Flour', 'Bread', 'Pasta'],
    recommended_dish: 'Warm Bone Broth Rice Congee with Soft Poached Egg',
    status: 'pending',
  },
  {
    id: 'plan-cured-meats',
    title: 'Processed Cured Meats Elimination',
    description: 'Pause bacon, sausages, salami, and cured meats containing concentrated nitrates, sulfites, and biogenic amines.',
    category: 'elimination',
    target_triggers: ['Processed Cured Meats'],
    avoid_ingredients: ['Bacon', 'Sausage', 'Salami', 'Ham', 'Chorizo', 'Nitrates'],
    recommended_dish: 'Pan-Roasted Cod with Fennel, Carrots & Steamed Potatoes',
    status: 'pending',
  },
  {
    id: 'plan-motility-walk',
    title: '15-Min Postprandial Motility Walk',
    description: 'Take a gentle 15-minute walk within 30 minutes of finishing your main meal to clear intestinal gas and stimulate healthy gastric emptying.',
    category: 'motility',
    target_triggers: [],
    avoid_ingredients: [],
    recommended_dish: 'Lemon-Herb Roast Turkey Cutlet with Sweet Potato Puree',
    status: 'pending',
  },
];

export const INITIAL_SAVED_RECIPES: SavedRecipe[] = [
  {
    id: 'recipe-risotto-1',
    title: 'CHICKEN AND MUSHROOM RISOTTO',
    portionSize: 1.0,
    category: 'Dinner',
    createdAtIso: new Date().toISOString(),
    nutrition: { calories: 588, protein: 42, fat: 21, carbs: 58, totalGrams: 400 },
    ingredients: [
      {
        id: 'ing-1',
        name: 'White Rice, Cooked',
        canonicalName: 'White Rice',
        amount: 1.15,
        unit: 'cup',
        gramsEstimate: 181.7,
        rating: 'low',
        calories: 241,
        protein: 5,
        fat: 1,
        carbs: 52,
      },
      {
        id: 'ing-2',
        name: 'Chicken Breast Boneless Skinless Cooked',
        canonicalName: 'Chicken',
        amount: 1,
        unit: 'serving',
        gramsEstimate: 100,
        rating: 'low',
        calories: 157,
        protein: 32,
        fat: 3,
        carbs: 0,
      },
      {
        id: 'ing-3',
        name: 'Oyster Mushrooms, Sautéed',
        canonicalName: 'Oyster Mushrooms',
        amount: 0.5,
        unit: 'cup',
        gramsEstimate: 50,
        rating: 'low',
        servingLimitNote: 'Low FODMAP up to 75g',
        calories: 25,
        protein: 2,
        fat: 0,
        carbs: 5,
        isCertifiedSwap: true,
      },
      {
        id: 'ing-4',
        name: 'Garlic-Infused Olive Oil',
        canonicalName: 'Garlic-Infused Olive Oil',
        amount: 1.05,
        unit: 'tbsp',
        gramsEstimate: 14.18,
        rating: 'low',
        servingLimitNote: 'Certified safe allium swap',
        calories: 124,
        protein: 0,
        fat: 14,
        carbs: 0,
        isCertifiedSwap: true,
      },
      {
        id: 'ing-5',
        name: 'Parmesan Cheese, Dry (Grated)',
        canonicalName: 'Parmesan Cheese',
        amount: 1.6,
        unit: 'tbsp',
        gramsEstimate: 10,
        rating: 'low',
        calories: 42,
        protein: 3,
        fat: 3,
        carbs: 1,
      },
    ],
  },
  {
    id: 'recipe-salad-1',
    title: 'FRESH MEDITERRANEAN CUCUMBER SALAD',
    portionSize: 1.0,
    category: 'Lunch',
    createdAtIso: new Date().toISOString(),
    nutrition: { calories: 195, protein: 7, fat: 15, carbs: 9, totalGrams: 220 },
    ingredients: [
      {
        id: 'ing-s1',
        name: 'Cucumber (Sliced)',
        canonicalName: 'Cucumber',
        amount: 1,
        unit: 'cup',
        gramsEstimate: 100,
        rating: 'low',
        calories: 15,
        protein: 1,
        fat: 0.1,
        carbs: 3,
      },
      {
        id: 'ing-s2',
        name: 'Common Tomato (Diced)',
        canonicalName: 'Common Tomato',
        amount: 0.5,
        unit: 'medium',
        gramsEstimate: 75,
        rating: 'low',
        calories: 18,
        protein: 0.9,
        fat: 0.2,
        carbs: 3.9,
      },
      {
        id: 'ing-s3',
        name: 'Feta Cheese (Crumbled)',
        canonicalName: 'Feta Cheese',
        amount: 0.33,
        unit: 'cup',
        gramsEstimate: 40,
        rating: 'low',
        calories: 105,
        protein: 5.6,
        fat: 8.5,
        carbs: 1.6,
      },
      {
        id: 'ing-s4',
        name: 'Olive Oil',
        canonicalName: 'Olive Oil',
        amount: 0.5,
        unit: 'tbsp',
        gramsEstimate: 7,
        rating: 'low',
        calories: 59,
        protein: 0,
        fat: 6.7,
        carbs: 0,
      },
    ],
  },
];

/**
 * Calibrates ingredient portion sizes for any meal, replacing uncalibrated 100g defaults
 * (such as this morning's granola toppings, seeds, honey, oils, aromatics) with realistic
 * culinary single-serving gram weights and populating the Monash safe limit note.
 */
export function calibrateMealIngredientPortions(meal: Meal): { meal: Meal; changed: boolean } {
  let changed = false;

  // If no dishes, ensureMealHasDishes creates them with realistic portions
  if (!meal.dishes || meal.dishes.length === 0) {
    const initialized = ensureMealHasDishes(meal);
    return { meal: initialized, changed: true };
  }

  const updatedDishes: PlateDish[] = meal.dishes.map((dish) => {
    let dishChanged = false;
    const updatedIngredients: MealIngredientItem[] = dish.ingredients.map((ing) => {
      const realistic = getRealisticCulinaryServing(ing.name);

      const cleanUnit = (ing.unit || '').toLowerCase().trim();
      const isMislabeledGramsAsHousehold =
        (cleanUnit === 'tbsp' || cleanUnit === 'tsp' || cleanUnit === 'cup' || cleanUnit === 'serving') &&
        (ing.amount || 0) >= 15 &&
        Math.abs((ing.amount || 0) - (ing.gramsEstimate || 0)) <= 1;

      // Detect if this ingredient defaulted to 100g with no explicit amount/unit
      const isUncalibratedWeight =
        (!ing.gramsEstimate && !ing.amount) ||
        (ing.gramsEstimate === 100 && !ing.amount && (!ing.unit || ing.unit === 'g'));

      const isMissingNote = !ing.servingLimitNote;
      const targetUnit = isMislabeledGramsAsHousehold
        ? 'g'
        : isUncalibratedWeight
        ? realistic.unit
        : (ing.unit || realistic.unit);
      const targetAmount = isMislabeledGramsAsHousehold
        ? (ing.gramsEstimate || ing.amount)
        : isUncalibratedWeight
        ? realistic.amount
        : ing.amount;
      const targetGrams = isUncalibratedWeight ? realistic.grams : (ing.gramsEstimate || realistic.grams);
      const targetNote = realistic.servingLimitNote || ing.servingLimitNote;

      const dynamicEval = getDynamicIngredientRating(ing.name, targetGrams);
      const targetRating = dynamicEval.rating;

      if (
        isMislabeledGramsAsHousehold ||
        isUncalibratedWeight ||
        isMissingNote ||
        ing.rating !== targetRating ||
        !ing.primaryCulprit && dynamicEval.primaryCulprit
      ) {
        dishChanged = true;
        changed = true;
        return {
          ...ing,
          amount: targetAmount,
          unit: targetUnit,
          gramsEstimate: targetGrams,
          rating: targetRating,
          primaryCulprit: dynamicEval.primaryCulprit || ing.primaryCulprit,
          servingLimitNote: targetNote,
        };
      }

      return ing;
    });

    if (dishChanged) {
      const mult = dish.portionMultiplier || 1.0;
      const totalGrams = Math.round(updatedIngredients.reduce((sum, i) => sum + (i.gramsEstimate || 0) * mult, 0));
      return {
        ...dish,
        ingredients: updatedIngredients,
        nutrition: {
          ...(dish.nutrition || { calories: 0, protein: 0, fat: 0, carbs: 0, totalGrams: 0 }),
          totalGrams,
        },
      };
    }
    return dish;
  });

  const stackingReport = calculatePlateFodmapStacking(updatedDishes, [], meal.timestamp_iso);
  const targetFodmapRating = stackingReport.overallPlateRating;

  if (meal.fodmap_rating !== targetFodmapRating || !meal.stacking_report) {
    changed = true;
  }

  if (changed) {
    const allIngredients = updatedDishes.flatMap((d) => d.ingredients);
    const totalGrams = Math.round(updatedDishes.reduce((sum, d) => sum + (d.nutrition?.totalGrams || 0), 0));
    return {
      meal: {
        ...meal,
        dishes: updatedDishes,
        ingredients: allIngredients,
        fodmap_rating: targetFodmapRating,
        stacking_report: stackingReport,
        nutrition: {
          ...(meal.nutrition || { calories: 0, protein: 0, fat: 0, carbs: 0, totalGrams: 0 }),
          totalGrams,
        },
      },
      changed: true,
    };
  }

  return { meal, changed: false };
}

/**
 * Normalizes any historical or legacy Meal so it always contains structured dishes,
 * ingredients with realistic culinary portions, and Monash safe limit notes.
 */
export function ensureMealHasDishes(meal: Meal): Meal {
  if (meal.dishes && meal.dishes.length > 0) {
    // Calibrate any existing dishes so uncalibrated 100g ingredients get realistic gram weights
    return calibrateMealIngredientPortions(meal).meal;
  }

  const ingredients = meal.ingredients && meal.ingredients.length > 0
    ? meal.ingredients
    : convertTagsToIngredientItems(meal.ingredients_tags || meal.description || 'Meal');

  // Ensure every ingredient has a realistic portion, dynamic rating, and servingLimitNote
  const calibratedIngredients = ingredients.map((ing) => {
    const realistic = getRealisticCulinaryServing(ing.name);
    const isUncalibrated = ing.gramsEstimate === 100 || !ing.gramsEstimate;
    const targetAmount = isUncalibrated ? realistic.amount : ing.amount;
    const targetUnit = isUncalibrated ? realistic.unit : (ing.unit || realistic.unit);
    const targetGrams = isUncalibrated ? realistic.grams : (ing.gramsEstimate || realistic.grams);
    const targetNote = ing.servingLimitNote || realistic.servingLimitNote;
    const dynamicEval = getDynamicIngredientRating(ing.name, targetGrams);

    return {
      ...ing,
      amount: targetAmount,
      unit: targetUnit,
      gramsEstimate: targetGrams,
      rating: dynamicEval.rating,
      primaryCulprit: dynamicEval.primaryCulprit || ing.primaryCulprit,
      servingLimitNote: targetNote,
    };
  });

  const totalGrams = calibratedIngredients.reduce((sum, i) => sum + (i.gramsEstimate || 0), 0);
  const totalCals = calibratedIngredients.reduce((sum, i) => sum + (i.calories || 0), 0);
  const totalP = calibratedIngredients.reduce((sum, i) => sum + (i.protein || 0), 0);
  const totalF = calibratedIngredients.reduce((sum, i) => sum + (i.fat || 0), 0);
  const totalC = calibratedIngredients.reduce((sum, i) => sum + (i.carbs || 0), 0);

  const defaultDish: PlateDish = {
    id: `dish-${meal.id}-1`,
    title: (meal.description || 'MAIN MEAL').toUpperCase(),
    portionMultiplier: meal.portionMultiplier || 1.0,
    ingredients: calibratedIngredients,
    nutrition: {
      calories: totalCals,
      protein: Math.round(totalP * 10) / 10,
      fat: Math.round(totalF * 10) / 10,
      carbs: Math.round(totalC * 10) / 10,
      totalGrams: Math.round(totalGrams),
    },
    notes: meal.notes,
  };

  const stackingReport = calculatePlateFodmapStacking([defaultDish], [], meal.timestamp_iso);

  return {
    ...meal,
    dishes: [defaultDish],
    ingredients: calibratedIngredients,
    fodmap_rating: stackingReport.overallPlateRating,
    stacking_report: stackingReport,
    nutrition: defaultDish.nutrition,
  };
}

